import { useState } from 'react';
import { t } from '../../i18n/index.js';
import { stageLabel } from './stage-label.js';
import { useSession } from '../../lib/session.js';
import { useApiData } from '../../lib/use-data.js';
import { ApiError, type AdminUser, type FlagState } from '../../lib/api.js';
import { FLAG_STAGES, isRetreat, reach, type FlagStage } from './stage-order.js';
import { PageHeader } from '../../patterns/PageHeader.js';
import { Badge } from '../../ui/Badge.js';
import { Banner } from '../../ui/Banner.js';
import { Button } from '../../ui/Button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/Card.js';
import { Field, Select } from '../../ui/Field.js';

/**
 * The rollout: what each feature is ready for, and who can see it.
 *
 * ── WHAT THIS SCREEN IS FOR, AND WHAT THE ACCOUNT PAGE IS FOR ─────────────
 *
 * Here you move a feature ALONG: off, internal, alpha, beta, generally
 * available. That changes who sees it everywhere at once.
 *
 * The account page does the other thing — one account, in or out, whatever the
 * rollout says. They are separate screens because they are separate decisions,
 * and mixing them is how somebody promotes a feature to everybody while meaning
 * to switch it on for one customer.
 */
export function FlagsPage() {
  const { api, me } = useSession();
  const [saving, setSaving] = useState<string | null>(null);
  /*
    A stage change that would REMOVE the feature from somebody waits for a
    second click.

    Moving a flag backwards is a legitimate thing to do in an incident, and a
    legitimate thing to do by accident when reaching for the wrong option in a
    list of five. The difference matters: it is the only change on this screen
    that can take a working feature away from an account that has it.
  */
  const [pending, setPending] = useState<{ key: string; to: FlagStage; from: FlagStage } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, reload } = useApiData(() => api.flags(), [api]);
  const { data: admins } = useApiData(() => api.admins(), [api]);

  const canWrite = me?.role === 'admin' || me?.role === 'super_admin';

  async function patch(key: string, body: Parameters<typeof api.setFlag>[1]) {
    setSaving(key);
    setError(null);
    try {
      await api.setFlag(key, body);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('flags.saveFailed'));
    } finally {
      setSaving(null);
    }
  }

  if (loading || !data) return null;

  if (!data.managedHere) {
    return (
      <>
        <PageHeader title={t('flags.title')} description={t('flags.body')} />
        <Banner intent="info" title={t('flags.elsewhereTitle')}>
          {t('flags.elsewhereBody', { provider: data.provider })}
        </Banner>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('flags.title')}
        description={t('flags.body')}
        actions={<Badge intent="neutral">{`${t('flags.provider')}: ${data.provider}`}</Badge>}
      />

      {error && <Banner intent="danger" title={t('flags.saveFailed')}>{error}</Banner>}

      <div className="flex flex-col gap-4">
        {data.flags.map((flag: FlagState) => {
          const r = reach(flag);
          return (
            <Card key={flag.key}>
              <CardHeader bordered>
                {/*
                  THE NAME LEADS, the key follows.

                  This read `<CardTitle>{flag.key}</CardTitle>`, so the screen
                  opened with `connectors.simulated` and said nothing about
                  what it does. The registry has carried a label and a
                  description the whole time; neither reached the API.

                  The key still belongs here — it is what somebody quotes when
                  reporting a problem, and two flags can read alike — but as a
                  subtitle.
                */}
                <CardTitle>{flag.label}</CardTitle>
                <p className="font-mono text-2xs text-tertiary">{flag.key}</p>
                <CardDescription>{flag.description}</CardDescription>
                <CardDescription>
                  {r.byStage === 'everyone'
                    ? t('flags.reachEveryone')
                    : r.byStage === 0
                      ? t('flags.reachNobody')
                      : t('flags.reachAccounts', { count: r.byStage })}
                  {/*
                    Overrides are reported beside the stage, never folded into
                    it. A flag at `off` with four accounts switched on is live
                    for four accounts, and a summary that said only "off" would
                    be hiding them.
                  */}
                  {r.forcedOn > 0 && `  ${t('flags.reachForcedOn', { count: r.forcedOn })}`}
                  {r.forcedOff > 0 && `  ${t('flags.reachForcedOff', { count: r.forcedOff })}`}
                </CardDescription>
              </CardHeader>

              <CardContent className="flex flex-col gap-4">
                {pending?.key === flag.key && (
                  <Banner intent="warning" title={t('flags.retreatWarning', { stage: stageLabel(pending.to) })}>
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={saving === flag.key}
                        onClick={() => {
                          const to = pending.to;
                          setPending(null);
                          void patch(flag.key, { stage: to });
                        }}
                      >
                        {t('flags.retreatConfirm')}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setPending(null)}>
                        {t('flags.retreatCancel')}
                      </Button>
                    </div>
                  </Banner>
                )}

                <div className="flex flex-wrap items-start gap-4">
                  <Field label={t('flags.stageLabel')} className="w-56">
                    {(field) => (
                      <Select
                        {...field}
                        value={pending?.key === flag.key ? pending.to : flag.stage}
                        disabled={!canWrite || saving === flag.key}
                        onChange={(e) => {
                          const to = e.target.value as FlagStage;
                          if (isRetreat(flag.stage, to)) setPending({ key: flag.key, to, from: flag.stage });
                          else void patch(flag.key, { stage: to });
                        }}
                      >
                        {FLAG_STAGES.map((s) => (
                          <option key={s} value={s}>
                            {stageLabel(s)}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </div>

                {/*
                  The user select, shown only where it applies.

                  `internal` is the one stage gated on the PERSON rather than
                  the account, because staff work inside customer accounts while
                  supporting them — gating on the account would either hide the
                  feature where it is being investigated or show it to that
                  customer's own users.
                */}
                {flag.stage === 'internal' && (
                  <div className="flex flex-col gap-2 rounded-(--control-radius) bg-sunken p-3">
                    <p className="text-sm font-medium text-primary">{t('flags.internalTitle')}</p>
                    <p className="text-sm text-tertiary">{t('flags.internalBody')}</p>

                    {flag.internalUserIds.length === 0 && (
                      <p className="text-sm text-tertiary">{t('flags.internalNone')}</p>
                    )}

                    <div className="flex flex-col gap-1">
                      {flag.internalUserIds.map((id) => {
                        const who = (admins ?? []).find((a: AdminUser) => a.id === id);
                        return (
                          <div key={id} className="flex flex-wrap items-center gap-2">
                            {/* The id is shown when the person is gone, rather
                                than a blank row that cannot be removed. */}
                            <span className="min-w-0 flex-1 text-md text-primary">
                              {who ? `${who.name ?? who.email} (${who.email})` : id}
                            </span>
                            {canWrite && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={saving === flag.key}
                                onClick={() =>
                                  void patch(flag.key, {
                                    internalUserIds: flag.internalUserIds.filter((x) => x !== id),
                                  })
                                }
                              >
                                {t('flags.removePerson')}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {canWrite && (
                      <Field label={t('flags.addPerson')} labelHidden className="w-full sm:w-96">
                        {(field) => (
                          <Select
                            {...field}
                            size="sm"
                            value=""
                            disabled={saving === flag.key}
                            onChange={(e) => {
                              if (!e.target.value) return;
                              void patch(flag.key, {
                                internalUserIds: [...flag.internalUserIds, e.target.value],
                              });
                            }}
                          >
                            <option value="">{t('flags.addPerson')}</option>
                            {(admins ?? [])
                              .filter((a: AdminUser) => !flag.internalUserIds.includes(a.id))
                              .map((a: AdminUser) => (
                                <option key={a.id} value={a.id}>
                                  {a.name ? `${a.name} (${a.email})` : a.email}
                                </option>
                              ))}
                          </Select>
                        )}
                      </Field>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}
